import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Card, CardHeader, CardContent } from '../../components/ui/Card';
import { Button } from '@/components/ui';
import { handleCheckoutSuccess } from '../../services/connect';

/**
 * CheckoutSuccess Page
 * 
 * Displayed after a successful Stripe Checkout session
 * Shows confirmation message and next steps
 */

const CheckoutSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [loading, setLoading] = useState(!!sessionId);
  const error = sessionId ? null : 'No session ID provided';

  useEffect(() => {
    if (!sessionId) {
      // Loading is already false when sessionId is null (initialized above)
      return;
    }

    // Call backend to handle checkout success
    // This is optional - you can expand this to record the purchase in your database
    handleCheckoutSuccess(sessionId)
      .then(() => {
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error handling checkout success:', err);
        // Still show success page even if backend call fails
        setLoading(false);
      });
  }, [sessionId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Processing your purchase...</p>
        </div>
      </div>
    );
  }

  if (error || !sessionId) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white dark:bg-gray-900">
        <Card className="max-w-md">
          <CardHeader>
            <h2 className="text-2xl font-bold text-red-600">Error</h2>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {error || 'Something went wrong. Please contact support if you were charged.'}
            </p>
            <Link to="/">
              <Button>Go Home</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-white dark:bg-gray-900 px-4">
      <Card className="max-w-2xl w-full">
        <CardHeader>
          <div className="text-center">
            {/* Success Icon */}
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 dark:bg-green-900/30 mb-4">
              <svg
                className="h-10 w-10 text-green-600 dark:text-green-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              Payment Successful!
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Thank you for your purchase
            </p>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Session Info */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
                Transaction ID
              </h3>
              <p className="text-sm font-mono text-gray-900 dark:text-white break-all">
                {sessionId}
              </p>
            </div>

            {/* What's Next */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                What's Next?
              </h3>
              <ul className="space-y-2 text-gray-700 dark:text-gray-300">
                <li className="flex items-start">
                  <svg
                    className="h-6 w-6 text-green-500 mr-2 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span>You'll receive a confirmation email shortly with your purchase details</span>
                </li>
                <li className="flex items-start">
                  <svg
                    className="h-6 w-6 text-green-500 mr-2 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span>The academy will contact you with lesson details and scheduling</span>
                </li>
                <li className="flex items-start">
                  <svg
                    className="h-6 w-6 text-green-500 mr-2 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span>If you have any questions, please contact the academy directly</span>
                </li>
              </ul>
            </div>

            {/* Important Note */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-2">
                Keep Your Receipt
              </h4>
              <p className="text-sm text-blue-800 dark:text-blue-200">
                Please save the email confirmation you receive. It contains important information
                about your purchase and serves as your receipt.
              </p>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <Link to="/" className="flex-1">
                <Button variant="outline" className="w-full">
                  Go to Home
                </Button>
              </Link>
              <Button
                onClick={() => window.print()}
                variant="outline"
                className="flex-1"
              >
                Print Receipt
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CheckoutSuccess;



















